/**
 * WebSocket → TanStack Query cache bridge (iter 258).
 *
 * Wires backend WS events directly into the TanStack Query cache. With
 * `broadcastQueryClient` in place (iter 257), a single WS event arriving
 * on one tab of one device updates the cache on THAT tab AND fans out to
 * all other tabs on the same device instantly. Other devices get the
 * event via their own WebSocket connection, so the end result is:
 *
 *   backend mutation → WS push → cache update on every tab of every
 *   device that has the app open, typically within <500ms.
 *
 * How to add a new event:
 *   1. Have the backend call `ws_manager.broadcast("thing.updated", {thing: doc})`.
 *   2. Add a handler below that either:
 *        - calls `queryClient.setQueryData(["key"], ...)` for a delta, OR
 *        - calls `queryClient.invalidateQueries({queryKey: ["key"]})` to trigger refetch.
 *   3. Done — no changes needed in pages/hooks.
 *
 * This file intentionally does NOT replace the existing Zustand-store
 * delta patches in useWebSocket.js. Both stay active: Zustand serves
 * pages not yet migrated to TanStack; TanStack serves pages that have
 * been migrated. The two paths are independent.
 */
import { queryClient } from "./queryClient";
import { toast } from "sonner";

let installed = false;

function handler(e) {
  const { event, data } = e.detail || {};
  if (!event || event === "pong") return;

  switch (event) {
    // ── Warranty claims ─────────────────────────────────────────
    case "warranty.claim_created": {
      const claim = data?.claim;
      if (!claim?.id) return;
      // Prepend to the claims list if present; otherwise invalidate.
      queryClient.setQueryData(["warranty", "claims"], (old) => {
        if (!Array.isArray(old)) return old;
        if (old.some((c) => c.id === claim.id)) return old;
        return [claim, ...old];
      });
      queryClient.setQueryData(["warranty", "claim", claim.id], claim);
      break;
    }
    case "warranty.claim_updated": {
      const claim = data?.claim;
      if (!claim?.id) return;
      queryClient.setQueryData(["warranty", "claims"], (old) =>
        Array.isArray(old) ? old.map((c) => (c.id === claim.id ? { ...c, ...claim } : c)) : old,
      );
      queryClient.setQueryData(["warranty", "claim", claim.id], claim);
      break;
    }
    case "warranty.claim_deleted": {
      const claimId = data?.claim_id;
      if (!claimId) return;
      queryClient.setQueryData(["warranty", "claims"], (old) =>
        Array.isArray(old) ? old.filter((c) => c.id !== claimId) : old,
      );
      queryClient.removeQueries({ queryKey: ["warranty", "claim", claimId] });
      break;
    }

    // ── Service invoice paid (iter 370gs) ───────────────────────
    case "service_invoice.paid": {
      const invNo = data?.invoice_number;
      const cust = data?.customer_name || "Customer";
      const amount = Number(data?.amount) || 0;
      toast.success(`💰 ${cust} paid $${amount.toFixed(2)}!`, {
        description: invNo ? `Invoice #${invNo} marked paid` : undefined,
        duration: 15000,
      });
      // Bust any open invoice queries so the editor reflects the new
      // status if it's open. The public viewer polls on its own.
      queryClient.invalidateQueries({ queryKey: ["service_invoices"] });
      break;
    }

    // ── Stock overview (admin) ──────────────────────────────────
    case "delivery.synced":
      // Stock snapshot changes after a sync — refresh the list + forecast.
      queryClient.invalidateQueries({ queryKey: ["stock"] });
      break;

    // ── Service call archive ────────────────────────────────────
    case "service_call.deleted":
    case "service_call.created":
    case "service_call.updated":
      // Zustand store still owns the service-calls list. Bump the archive
      // stat card so it reflects the change.
      queryClient.invalidateQueries({ queryKey: ["admin-archive"] });
      break;

    // ── Street-legal / kanban ───────────────────────────────────
    case "street_legal.packet_created":
    case "street_legal.packet_updated":
    case "street_legal.pdf_ready":
      // The kanban hook owns its own cache (KANBAN_CACHE_KEY) and applies
      // in-place WS deltas there. Keep this bridge a no-op for those —
      // the dedicated handler already does the right thing.
      break;

    default:
      break;
  }
}

/** Install the bridge once. Call from a top-level component (e.g. Layout). */
export function installWsCacheBridge() {
  if (installed) return;
  installed = true;
  window.addEventListener("ws_event", handler);
}

/** Test helper — remove the listener. */
export function uninstallWsCacheBridge() {
  installed = false;
  window.removeEventListener("ws_event", handler);
}
